export type SupportRequestKind = 'WALKER_REASSIGN' | 'TRAINER_RESCHEDULE' | 'VENDOR_NO_SHOW';
export type SupportRequestStatus = 'OPEN' | 'PROPOSED' | 'RESOLVED' | 'REFUNDED' | 'CANCELLED';

export type SupportRequest = {
  id: string;
  booking_id: string;
  /**
   * Any module, not just the two with bespoke recovery paths — a vendor no-show can be raised
   * on a vet consult or a cremation pickup just as easily as on a walk.
   */
  module_type: string;
  kind: SupportRequestKind;
  status: SupportRequestStatus;
  opened_by: string;
  reason: string | null;
  proposed_slot_start: string | null;
  proposed_slot_end: string | null;
  trainer_agreed: boolean;
  user_agreed: boolean;
  reassigned_to_vendor_profile_id: string | null;
  payout_amount_inr: string | null;
  refund_amount_inr: string | null;
  resolved_at: string | null;
  created_at: string;
  updated_at: string;
  /**
   * How long this has been somebody's problem — still counting while open,
   * frozen at resolution once closed.
   *
   * Computed server-side so the row, the tile and the CSV cannot drift apart.
   */
  waiting_days: number;
  is_open: boolean;
  booking: {
    id: string;
    status: string;
    /** What was booked, not just which recovery path this is. */
    service_name: string | null;
    pet: { id: string; name: string; species: string } | null;
    slot_start: string;
    slot_end: string;
    price_inr: string;
    payout_amount_inr: string | null;
    vendor_profile_id: string;
    vendor_business_name: string | null;
    user_id: string;
    customer_name: string | null;
    /** OTP-only accounts have no name, so this is usually the only identifier. */
    customer_phone: string | null;
    contact_phone: string | null;
    pickup_address: string | null;
    /** The walker currently on this booking, to compare candidates against. */
    current_walker: WalkerIdentity | null;
    current_vendor_rating: string | null;
    current_vendor_reviews: number | null;
    current_vendor_completed_walks: number | null;
    current_business_photo_path: string | null;
    walk_plan_id: string | null;
    training_package_id: string | null;
  };
};

export type PayoutRollupRow = {
  vendor_profile_id: string;
  vendor_business_name: string | null;
  walks: number;
  amount_inr: string;
};

export type SupportRequestDetail = SupportRequest & { payout_rollup: PayoutRollupRow[] | null };

/**
 * The person who would actually turn up, as distinct from the business.
 *
 * A walker vendor is two records: the VendorProfile (the business, which gets
 * paid) and the PetWalkerProfile (the human, who arrives at somebody's door to
 * take their dog). Reassignment is a decision about the second one.
 */
export type WalkerIdentity = {
  full_name: string;
  age: number | null;
  gender: string | null;
  years_of_experience: number;
  specializations: string[];
  languages: string[];
  /** Off means nothing proves the walk started or ended. Rare, and worth seeing. */
  require_walk_otp: boolean;
  photo_path: string | null;
};

export type NearbyWalker = {
  vendor_profile_id: string;
  business_name: string | null;
  city: string | null;
  distance_km: number | null;
  /** The walker's own declared service radius, in km. */
  service_radius_km: number;
  /**
   * Whether that radius actually reaches this booking. Candidates outside it are
   * still offered — a fallen-through booking needs options — but flagged, so the
   * admin knows they are asking someone to travel further than they advertise.
   */
  covers_distance: boolean;
  /** Something to choose on when several candidates sit at the same distance. */
  avg_rating: string;
  review_count: number;
  completed_walks: number;
  /** Null only if the vendor has no walker profile, which should not happen. */
  walker: WalkerIdentity | null;
  /**
   * The business's own photo, as distinct from the walker's face.
   *
   * Both matter: the face is who turns up, the shopfront is what the customer
   * was shown when they booked.
   */
  business_photo_path: string | null;
};

export type NearbyWalkersResponse = {
  center: { lat: number; lng: number } | null;
  search_radius_km: number;
  items: NearbyWalker[];
};

export type SupportRequestListResponse = {
  items: SupportRequest[];
  total: number;
  page: number;
  page_size: number;
  open_count: number;
};

/** Server-side sort keys. Anything not here is not offered in the UI. */
export const SUPPORT_SORT_FIELDS = ['created_at', 'updated_at', 'status', 'kind'] as const;

/** Statuses that mean somebody is still waiting on us. */
export const OPEN_SUPPORT_STATUSES: SupportRequestStatus[] = ['OPEN', 'PROPOSED'];

export const SUPPORT_KIND_LABELS: Record<SupportRequestKind, string> = {
  WALKER_REASSIGN: 'Find another walker',
  TRAINER_RESCHEDULE: 'Move the session',
  VENDOR_NO_SHOW: 'Vendor never showed',
};

export const SUPPORT_STATUS_LABELS: Record<SupportRequestStatus, string> = {
  OPEN: 'Open',
  PROPOSED: 'Awaiting agreement',
  RESOLVED: 'Resolved',
  REFUNDED: 'Refunded',
  CANCELLED: 'Cancelled',
};

/** Who raised it. SYSTEM is the no-show sweep, not a person. */
export const OPENED_BY_LABELS: Record<string, string> = {
  USER: 'Customer',
  VENDOR: 'Vendor',
  SYSTEM: 'Automatic',
  ADMIN: 'Admin',
};

/** Tiles on the queue, each of which is also a filter it can apply. */
export type SupportCounts = {
  total: number;
  open: number;
  proposed: number;
  resolved: number;
  refunded: number;
  cancelled: number;
  /** Days the longest-waiting open request has been waiting. Null when none. */
  oldest_open_days: number | null;
  open_over_7_days: number;
  open_without_reason: number;
  refunded_inr: string;
};

export type SupportRequestListFilters = {
  q?: string;
  /** Several statuses at once, comma-separated. Beats `status` when both are set. */
  statuses?: string;
  opened_by?: string;
  user_id?: string;
  /** '' = either, 'true' / 'false' = that side only. Matches the URL tri-state. */
  has_reason?: string;
  /** Only open requests that have been waiting longer than this. */
  waiting_over_days?: number;
  page?: number;
  page_size?: number;
  kind?: SupportRequestKind;
  status?: SupportRequestStatus;
  module_type?: string;
  /** Inclusive created-date range, YYYY-MM-DD. */
  from?: string;
  to?: string;
  /** Narrow to one vendor. */
  vendor_profile_id?: string;
};

export const SUPPORT_KIND_OPTIONS: { value: SupportRequestKind; label: string }[] = [
  { value: 'WALKER_REASSIGN', label: 'Walker reassignment' },
  { value: 'TRAINER_RESCHEDULE', label: 'Trainer reschedule' },
  { value: 'VENDOR_NO_SHOW', label: 'Vendor no-show' },
];

/**
 * One label per kind, exhaustive by type.
 *
 * The list and detail screens each carried their own two-way ternary, so a third kind rendered
 * as "Trainer reschedule" — a vendor no-show on a vet booking was labelled a trainer reschedule
 * in the admin queue. A Record keyed by the union makes the next addition a compile error
 * instead of a silent mislabel.
 */
export const SUPPORT_KIND_LABEL: Record<SupportRequestKind, string> = {
  WALKER_REASSIGN: 'Walker reassignment',
  TRAINER_RESCHEDULE: 'Trainer reschedule',
  VENDOR_NO_SHOW: 'Vendor no-show',
};

/** Compact form for a table cell, where the column is narrow. */
export const SUPPORT_KIND_SHORT_LABEL: Record<SupportRequestKind, string> = {
  WALKER_REASSIGN: 'Walker reassign',
  TRAINER_RESCHEDULE: 'Trainer reschedule',
  VENDOR_NO_SHOW: 'Vendor no-show',
};

export const SUPPORT_STATUS_OPTIONS: { value: SupportRequestStatus; label: string }[] = [
  { value: 'OPEN', label: 'Open' },
  { value: 'PROPOSED', label: 'Proposed' },
  { value: 'RESOLVED', label: 'Resolved' },
  { value: 'REFUNDED', label: 'Refunded' },
  { value: 'CANCELLED', label: 'Cancelled' },
];
